/**
 * Stripe.js, loaded from config.stripeJsUrl: Stripe's own script, or the payments stub's
 * stand-in for it while the firm waits for Stripe (apps/payments-stub). The app only uses
 * the parts typed here, which the stub implements too, so switching is a config change.
 */
import { isDark } from "@taxi/mobile-web/config";

export type StripeError = {
  type: string;
  code?: string;
  decline_code?: string;
  message?: string;
};

type Intent = { id: string; status: string };
export type PaymentResult = { error?: StripeError; paymentIntent?: Intent };
export type SetupResult = { error?: StripeError; setupIntent?: Intent };

export type ElementChange = { complete: boolean; empty: boolean };

export interface PaymentElement {
  mount(target: HTMLElement): void;
  destroy(): void;
  on(event: "ready", handler: () => void): void;
  on(event: "change", handler: (event: ElementChange) => void): void;
}

export interface Elements {
  create(
    type: "payment",
    options?: {
      layout?: "tabs" | "accordion";
      wallets?: { applePay?: "auto"; googlePay?: "auto" };
    },
  ): PaymentElement;
}

export type Appearance = {
  theme?: "stripe" | "night" | "flat";
  variables?: Record<string, string>;
};

export interface Stripe {
  elements(options: { clientSecret: string; appearance?: Appearance }): Elements;
  confirmPayment(options: {
    elements: Elements;
    confirmParams: { return_url: string };
    redirect: "if_required";
  }): Promise<PaymentResult>;
  confirmSetup(options: {
    elements: Elements;
    confirmParams: { return_url: string };
    redirect: "if_required";
  }): Promise<SetupResult>;
  /** A saved card (by id), or the card already on the payment when it needs the customer. */
  confirmCardPayment(
    clientSecret: string,
    data?: { payment_method?: string },
  ): Promise<PaymentResult>;
  confirmCardSetup(clientSecret: string, data?: { payment_method?: string }): Promise<SetupResult>;
}

type StripeFactory = (publishableKey: string) => Stripe;

declare global {
  interface Window {
    Stripe?: StripeFactory;
  }
}

let loading: Promise<StripeFactory> | undefined;

function loadScript(url: string): Promise<StripeFactory> {
  loading ??= new Promise<StripeFactory>((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () =>
      window.Stripe ? resolve(window.Stripe) : reject(new Error("The payment form didn't load"));
    script.onerror = () => {
      loading = undefined;
      script.remove();
      reject(new Error("The payment form didn't load. Check your connection and try again."));
    };
    document.head.appendChild(script);
  });
  return loading;
}

const instances = new Map<string, Stripe>();

export async function loadStripe(url: string, publishableKey: string): Promise<Stripe> {
  const factory = await loadScript(url);
  let stripe = instances.get(publishableKey);
  if (!stripe) {
    stripe = factory(publishableKey);
    instances.set(publishableKey, stripe);
  }
  return stripe;
}

/** The payment form in the firm's colours, light or dark like the rest of the app. */
export function appearance(primary: string): Appearance {
  const dark = isDark();
  return {
    theme: dark ? "night" : "stripe",
    variables: {
      colorPrimary: primary,
      borderRadius: "12px",
      fontFamily: "Plus Jakarta Sans, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      fontSizeBase: "16px",
    },
  };
}

/** A message for the customer from a Stripe error. */
export function stripeMessage(error: StripeError): string {
  return error.message || "Your card couldn't be used. Try another card.";
}
